import { z } from "zod";

import { solutionRecommendationsSchema } from "./solution-recommendations.js";

/**
 * The Analyst's run summary. Lives in `jobs.workerResult.artifacts[0]`
 * once the orchestrator persists the agent's `run_complete` payload via
 * `applyAgentOutput`.
 *
 * The agent-type is `"analyst"`; `expectsValidator` on the envelope stays
 * `false` until issue #143 (Match Validator) ships. With
 * `expectsValidator: false` the orchestrator transitions
 * `running → completed` directly with audit reason
 * `worker_skipped_validator`.
 *
 * `recommendations` is the **same payload** that was Zod-parsed via
 * {@link solutionRecommendationsSchema} immediately before being inserted
 * into `solution_presentations.recommendations`. Re-exposing it on the
 * artifact gives the admin-ui's live-run view a shape-validated copy
 * without a join to the presentation row, and gives the future Match
 * Validator (#143) a stable read surface.
 *
 * `customProposalDriveFileId` / `customProposalDriveWebViewLink` are
 * populated only when `recommendations.solution_type` is
 * `"custom_build"` or `"hybrid"` AND the Drive upload succeeded. A
 * Drive-side failure is non-fatal: the row still lands, the artifact
 * surfaces the failure via the envelope's `errors`, these two fields
 * stay `null`, and an operator can re-trigger via a manual dispatch.
 */
export const analystArtifactSchema = z.object({
  /** Discriminator carried for forward-compat. */
  kind: z.literal("solution_recommendation"),
  /** The `problem_memos.id` the Analyst just judged. */
  memoId: z.string().uuid(),
  /** The newly-inserted `solution_presentations.id`. */
  presentationId: z.string().uuid(),
  /** The same payload written to `solution_presentations.recommendations`. */
  recommendations: solutionRecommendationsSchema,
  /** Drive file id of the custom-proposal Google Doc; null when the
   *  solution_type is `tool_assembly` OR the upload failed. */
  customProposalDriveFileId: z.string().nullable(),
  /** Web-view link for the custom-proposal Google Doc; null in the same
   *  cases as `customProposalDriveFileId`. */
  customProposalDriveWebViewLink: z.string().url().nullable(),
  /**
   * Forensic snapshot of the `agent_config!A:B` rows that drove this
   * run. Mirrors the intake-validator artifact — thresholds + model id
   * frozen so a later audit can interpret the verdict against the
   * templating values in force at the time.
   */
  agentConfigSnapshot: z.record(z.union([z.string(), z.number()])),
  /**
   * Git SHA of the seed file
   * `packages/prompts/src/analyst/default.md` at run time, or `"dev"`
   * when running from an uncommitted tree. The seed is the source for
   * `agent_prompts.analyst.default` — the live prompt that drove the
   * run is in `jobs.promptsSnapshot["default"]`.
   */
  promptVersion: z.string().min(1),
  startedAt: z.string().datetime(),
  finishedAt: z.string().datetime(),
});

export type AnalystArtifact = z.infer<typeof analystArtifactSchema>;
